import { FileShort } from '@simOn/common/models';
import { DocumentDetails } from '@simOn/documents/element/models';
import { EnumValueGetter } from '@simOn/utils/models';

/**
 * Defines how an asset category icon is rendered (maps to backend `AssetCategoryIconStyle`).
 * The API serializes this enum as its string names.
 */
export const AssetCategoryIconStyle = {
  Unknown: 'Unknown',
  BlackWhite: 'BlackWhite',
  CustomColor: 'CustomColor',
  CustomImage: 'CustomImage'
} as const;

export type AssetCategoryIconStyleKeys = keyof typeof AssetCategoryIconStyle;
export type AssetCategoryIconStyleValues = EnumValueGetter<typeof AssetCategoryIconStyle>;

/** Defines system-provided asset categories (maps to backend `PredefinedAssetCategory`). */
export const PredefinedAssetCategory = {
  None: 0,
  Multimedia: 1,
  HomeAppliances: 2,
  OfficeEquipment: 3,
  Furniture: 4,
  Decoration: 5,
  LightingSystems: 6,
  Plants: 7,
  Documents: 8,
  Electrical: 9,
  Security: 10,
  FireSafety: 11,
  Plumbing: 12,
  SanitaryAndHygiene: 13,
  HVACSystems: 14,
  RES: 15,
  Hobby: 16,
  Transport: 17,
  Education: 18,
  Tools: 19,
  Garden: 20,
  Machines: 21,
  SpareParts: 22,
  Pets: 23,
  Kids: 24,
  FinishingMaterials: 25,
  Construction: 26,
  Other: 27
} as const;

export type PredefinedAssetCategoryKeys = keyof typeof PredefinedAssetCategory;
export type PredefinedAssetCategoryValues = EnumValueGetter<typeof PredefinedAssetCategory>;

/** Represents an asset category (maps to backend `AssetCategoryDto`). */
export interface AssetCategory {
  id: string;
  apartmentId: string;
  name: string | null;
  iconStyle: AssetCategoryIconStyleValues;
  icon: string | null;
  iconColor: string | null;
  iconColorBackground: string | null;
  imageId: string | null;
  image: DocumentDetails | null;
  imageUrlWithSasToken: string | null;
  scale: number | null;
  /** The API serializes some enums as string names, so this may arrive as `27` or `"Other"`. */
  predefinedCategory: PredefinedAssetCategoryValues | PredefinedAssetCategoryKeys;
  isPredefined: boolean;
}

/** DTO used to create an asset category (maps to backend `CreateAssetCategoryDto`). */
export interface CreateAssetCategory {
  name: string;
  iconStyle: AssetCategoryIconStyleValues;
  icon?: string | null;
  iconColor?: string | null;
  iconColorBackground?: string | null;
  imageId?: string | null;
  /** Custom image attachment data (maps to backend `DocumentAttachmentShortDto`); sent when a new image is picked. */
  image?: FileShort | null;
  scale?: number | null;
}

/** DTO used to update an asset category (maps to backend `UpdateAssetCategoryDto`). */
export interface UpdateAssetCategory extends CreateAssetCategory {
  id: string;
}
